import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useGetPageTextContent } from "~/api/documents/documents";
import { useGetKnowledgebasePageContent } from "~/api/knowledge/knowledge";
import type { Language, ReportSection } from "~/api/model";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { Accordion } from "~/components/ui/accordion";
import { Skeleton } from "~/components/ui/skeleton";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { useChatDocumentParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import {
  BatesLink,
  SectionLink,
  TERMS_PROSE_CLASS,
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  getTermsAndSectionsMarkdownComponents,
  highlightTermsAndSectionsInMarkdown,
  normalizeLiteralNewlines,
  unwrapAllCodeBlocks,
  useSourceMapDelegation,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { getInitialExpandedSections } from "./getInitialExpandedSections";
import { ReportSectionView } from "./ReportSection";
import { SourceReference } from "./ReportSectionSources";
import { OutdatedReason } from "./TemplateReportSelector";

export type ReportViewStatus =
  | { type: "loading" }
  | { type: "processing" }
  | { type: "pending" }
  | { type: "ready" }
  | { type: "outdated"; reason?: OutdatedReason }
  | { type: "templateDeleted" };

type ReportViewProps = {
  reportSections: ReportSection[];
  status: ReportViewStatus;
  terms: Hint[];
  sectionLinks?: SectionLink[];
  content: string;
  onRegenerateSection: (sectionId: string) => void;
  onEditSection: (sectionId: string, content: string) => Promise<void>;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionsAvailable?: boolean;
  onSectionClick?: (sectionId: string, firstPageId: string) => void;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
};

export const ReportView = ({
  reportSections,
  status,
  terms,
  sectionLinks = [],
  content,
  onRegenerateSection,
  onEditSection,
  templateName,
  outputLanguage,
  isActionsAvailable = true,
  onSectionClick,
  batesLinks,
  onBatesClick,
}: ReportViewProps) => {
  const { organizationId, caseId } = useChatDocumentParams();
  const { docList, pageItems, absolutePageNumbers } = useDocList();

  const reportContainerRef = useRef<HTMLDivElement>(null);
  useSourceMapDelegation(reportContainerRef, onSectionClick, onBatesClick);

  // Source modal state
  const [pageTextModalOpen, setPageTextModalOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  // Knowledge base source state
  const [selectedKbDocumentId, setSelectedKbDocumentId] = useState<
    string | null
  >(null);
  const [selectedKbPageNumber, setSelectedKbPageNumber] = useState<
    number | null
  >(null);
  const [isKnowledgeBaseSource, setIsKnowledgeBaseSource] = useState(false);

  const getSectionValue = useCallback(
    (section: ReportSection, index: number) => section.id ?? `section-${index}`,
    []
  );

  const sortedSections = useMemo(
    () => [...reportSections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [reportSections]
  );

  const [expandedSections, setExpandedSections] = useState<string[]>(() =>
    getInitialExpandedSections(
      sortedSections.map((s, i) => getSectionValue(s, i))
    )
  );

  const knownSectionIds = useRef<Set<string>>(
    new Set(sortedSections.map((s, i) => getSectionValue(s, i)))
  );

  useEffect(() => {
    const currentIds = sortedSections.map((s, i) => getSectionValue(s, i));
    const currentSet = new Set(currentIds);
    const hasRemovedIds = [...knownSectionIds.current].some(
      (id) => !currentSet.has(id)
    );
    if (hasRemovedIds) {
      setExpandedSections(getInitialExpandedSections(currentIds));
    } else {
      const trulyNewIds = currentIds.filter(
        (id) => !knownSectionIds.current.has(id)
      );
      if (trulyNewIds.length > 0) {
        setExpandedSections((prev) => [...prev, ...trulyNewIds]);
      }
    }
    knownSectionIds.current = currentSet;
  }, [sortedSections, getSectionValue]);

  // Fetch case document page content
  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(organizationId, caseId, selectedPageId ?? "", {
    query: {
      enabled:
        !isKnowledgeBaseSource &&
        !!selectedPageId &&
        !!caseId &&
        selectedPageId !== "unknown" &&
        organizationId !== "unknown",
    },
  });

  // Fetch knowledge base document page content
  const {
    data: kbPageContentData,
    isLoading: isKbPageContentLoading,
    error: kbPageContentError,
  } = useGetKnowledgebasePageContent(
    organizationId,
    selectedKbDocumentId ?? "",
    selectedKbPageNumber ?? 0,
    {
      query: {
        enabled:
          isKnowledgeBaseSource &&
          !!selectedKbDocumentId &&
          !!selectedKbPageNumber &&
          selectedKbPageNumber > 0 &&
          organizationId !== "unknown",
      },
    }
  );

  const handleSourceClick = useCallback(
    async (source: SourceReference) => {
      trackEvent({
        eventName: "app/report/section_source.badge/click",
        properties: {
          isKnowledgeBase: source.isKnowledgeBase === true,
          documentId: source.documentId,
        },
      });
      const isKbSource = source.isKnowledgeBase === true;

      if (isKbSource) {
        // Knowledge base source - pageId is the page number, documentId is the knowledgeDocumentId
        const pageNumber = parseInt(source.pageId, 10);
        if (!isNaN(pageNumber) && source.documentId) {
          setIsKnowledgeBaseSource(true);
          setSelectedKbDocumentId(source.documentId);
          setSelectedKbPageNumber(pageNumber);
          setSelectedPageId(null);
          setPageImageUrl(undefined);
          setPageRotation(0);
          setIsContentLoading(false);
          setPageTextModalOpen(true);
        }
      } else {
        // Case document source - pageId is the page UUID
        setIsKnowledgeBaseSource(false);
        setSelectedKbDocumentId(null);
        setSelectedKbPageNumber(null);
        setSelectedPageId(source.pageId);
        setIsContentLoading(true);
        setPageTextModalOpen(true);

        const pageItem = pageItems.find((p) => p.pageId === source.pageId);
        if (pageItem) {
          setPageRotation(pageItem.rotate);

          try {
            const urls = await pageItem.getUrls();
            setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
          } catch {
            setPageImageUrl(undefined);
          } finally {
            setIsContentLoading(false);
          }
        } else {
          setIsContentLoading(false);
        }
      }
    },
    [pageItems]
  );

  const modalTitle = useMemo(() => {
    if (isKnowledgeBaseSource) {
      return `${kbPageContentData?.filename ?? "Knowledge Base Document"} - Page ${selectedKbPageNumber}`;
    }

    if (!selectedPageId) return "Page Content Recognition";

    const pageItem = pageItems.find((p) => p.pageId === selectedPageId);
    if (!pageItem) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === pageItem.sectionId);

    const sectionName = section?.name || "Unknown Section";
    const pageNumber = absolutePageNumbers[selectedPageId] || 0;

    return `${sectionName} – Page ${pageNumber}`;
  }, [
    isKnowledgeBaseSource,
    kbPageContentData?.filename,
    selectedKbPageNumber,
    selectedPageId,
    pageItems,
    docList,
    absolutePageNumbers,
  ]);

  const highlightedContent = useMemo(() => {
    if (!content) return "";
    const preprocessed = ensureBoldHeadingSpacing(
      ensureMarkdownNewlines(
        unwrapAllCodeBlocks(normalizeLiteralNewlines(content))
      )
    );
    return highlightTermsAndSectionsInMarkdown(
      preprocessed,
      terms,
      sectionLinks,
      batesLinks
    );
  }, [content, terms, sectionLinks, batesLinks]);

  if (
    (status.type === "loading" || status.type === "processing") &&
    reportSections.length === 0
  ) {
    return (
      <div className="max-w-sm animate-pulse">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            {index % 2 === 0 ? (
              <Skeleton className="h-4 bg-muted rounded-full w-48 mb-4" />
            ) : (
              <Skeleton className="h-4 bg-muted rounded-full w-32 mb-4" />
            )}
            <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-6" />
          </div>
        ))}
      </div>
    );
  }

  if (reportSections.length > 0) {
    return (
      <div ref={reportContainerRef} className="flex flex-col gap-4">
        <Accordion
          type="multiple"
          value={expandedSections}
          onValueChange={(newValue: string[]) => {
            const expanded = newValue.filter(
              (v) => !expandedSections.includes(v)
            );
            const collapsed = expandedSections.filter(
              (v) => !newValue.includes(v)
            );
            if (expanded.length > 0 || collapsed.length > 0) {
              trackEvent({
                eventName: "app/report/section_accordion.trigger/click",
                properties: {
                  expanded: expanded.length > 0 ? expanded : undefined,
                  collapsed: collapsed.length > 0 ? collapsed : undefined,
                },
              });
            }
            setExpandedSections(newValue);
          }}
        >
          {sortedSections.map((section, index) => {
            const sectionValue = getSectionValue(section, index);
            return (
              <ReportSectionView
                key={sectionValue}
                reportSection={section}
                accordionValue={sectionValue}
                terms={terms}
                sectionLinks={sectionLinks}
                onRegenerateSection={onRegenerateSection}
                onEditSection={onEditSection}
                status={section.status}
                templateName={templateName}
                outputLanguage={outputLanguage}
                isActionAvailable={isActionsAvailable}
                onSourceClick={handleSourceClick}
                onSectionClick={onSectionClick}
                batesLinks={batesLinks}
                onBatesClick={onBatesClick}
              />
            );
          })}
        </Accordion>

        {pageTextModalOpen && (
          <MarkdownContentModal
            title={modalTitle}
            content={
              isKnowledgeBaseSource
                ? isKbPageContentLoading
                  ? "Loading content..."
                  : kbPageContentError
                    ? "Error loading content. This page may not have processed content available yet."
                    : kbPageContentData?.content ||
                      "No content available for this page."
                : isPageContentLoading || isContentLoading
                  ? "Loading content..."
                  : pageContentError
                    ? "Error loading content. This page may not have processed content available yet."
                    : pageContentData?.content ||
                      "No content available for this page."
            }
            filename={
              isKnowledgeBaseSource
                ? kbPageContentData?.filename
                : pageContentData?.filename
            }
            authorName={
              isKnowledgeBaseSource
                ? (kbPageContentData?.authorName ?? undefined)
                : pageContentData?.authorName
            }
            uploadDate={
              isKnowledgeBaseSource
                ? kbPageContentData?.uploadDate
                : pageContentData?.uploadDate
            }
            open={pageTextModalOpen}
            onOpenChange={(open) => {
              setPageTextModalOpen(open);
              if (!open) {
                // Reset state when modal closes
                setIsKnowledgeBaseSource(false);
                setSelectedKbDocumentId(null);
                setSelectedKbPageNumber(null);
                setSelectedPageId(null);
                setPageImageUrl(undefined);
                setPageRotation(0);
              }
            }}
            copyEventName="app/report/page_text_modal/copy_all.button/click"
            exportOpenEventName="app/report/page_text_modal/export.button/click"
            exportEventName="app/report/page_text_modal/export_menu_open/export_action.link/click"
            terms={terms}
            pageImageUrl={
              isKnowledgeBaseSource
                ? (kbPageContentData?.scanUrl ?? undefined)
                : pageImageUrl
            }
            pageRotation={isKnowledgeBaseSource ? 0 : pageRotation}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={reportContainerRef}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        className={TERMS_PROSE_CLASS}
        components={{
          ...getTermsAndSectionsMarkdownComponents(
            false, // isSummary
            terms,
            sectionLinks
          ),
          h1: (props) => (
            <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
          ),
          h2: (props) => <h2 {...props} className="text-base font-bold mb-1" />,
          h3: (props) => (
            <h3 {...props} className="text-sm font-medium uppercase mb-1" />
          ),
        }}
      >
        {highlightedContent}
      </Markdown>
    </div>
  );
};
